import {tokenDefinition} from "../../token/tokenDefinition.mjs";
import {TokenType} from "../../token/tokenType.mjs";
import {TokenName} from "./tokenName.mjs";
import {NodeName} from "./nodeName.mjs";
import {Node} from "../../node/node.mjs";

const emphasis = tokenDefinition({
    name: TokenName.EMPHASIS,
    type: TokenType.DELIMITER,
    tokenMatcher: /^\*/,
    contentMatcher: /^\*+/
});

class Emphasis {

    constructor() {
        this.length = 0
    }

    consume(tokenIterator, nodeIterator) {

        if(this.isOpening(tokenIterator)) {
            this.openNewNode(tokenIterator, nodeIterator)
        } else if(this.isClosing(tokenIterator, nodeIterator)) {
            this.closeNode(tokenIterator, nodeIterator)
        } else if(this.isCanceling(tokenIterator)) {
            this.cancelAllOpenEmphasis(nodeIterator)
        }
    }

    onEndOfMarkup(openNode) {
        openNode.cancel()
    }

    toHtml(node, toHtml, idProvider) {
        var content = ""
        for(const child of node.children) {
            content += toHtml(child, idProvider)
        }

        if(node.name === NodeName.EMPHASIS_BOLD) return "<strong>" + content + "</strong>"
        else if(node.name === NodeName.EMPHASIS_ITALIC) return "<i>" + content + "</i>"
        else if(node.name === NodeName.EMPHASIS_BOLD_ITALIC) return "<strong><i>" + content + "</i></strong>"
    }

    isOpening(tokenIterator) {
        return tokenIterator.token().name === TokenName.EMPHASIS
            && ( tokenIterator.isFirst() || this.isPrecededByDelimiter(tokenIterator) )
    }

    isClosing(tokenIterator, nodeIterator) {
        return tokenIterator.token().name === TokenName.EMPHASIS
            && (tokenIterator.isLast() || this.isFollowedByDelimiter(tokenIterator))
            && this.hasBeenOpen(nodeIterator);
    }

    hasBeenOpen(nodeIterator) {
        return nodeIterator.indexOfLastOpen(NodeName.EMPHASIS_UNDEFINED) !== null;
    }

    isCanceling(tokenIterator) {
        return tokenIterator.token().name === TokenName.LINE_FEED
    }

    openNewNode(tokenIterator, nodeIterator) {

        // create rule to track number of * open
        const emphasisRule = new Emphasis()
        emphasisRule.length = tokenIterator.token().value.length
        emphasisRule.isOpen = true

        // open undefined emphasis node
        const newOpenNode = new Node(
            NodeName.EMPHASIS_UNDEFINED,
            tokenIterator.token().value,
            emphasisRule,
            nodeIterator.currentParentNode
        )

        nodeIterator.addOpenNode(newOpenNode)
        tokenIterator.eatTokens(1)
    }

    closeNode(tokenIterator, nodeIterator) {
        // close all the node that can be closed
        // open node with remaining token value

        for(let openNodeIndex = nodeIterator.indexOfLastOpen(NodeName.EMPHASIS_UNDEFINED);
            openNodeIndex !== null;
            openNodeIndex = nodeIterator.indexOfLastOpen(NodeName.EMPHASIS_UNDEFINED)) {

            const openNode = nodeIterator.openNodes[openNodeIndex]

            // calculate the number of emphasis being closed for this node
            let emphasisToClose = tokenIterator.token().value.length
            const emphasisOpenCount = openNode.value.length
            const emphasisClosable = this.calculateNumberOfDelimiterClosed(emphasisOpenCount, emphasisToClose)

            // determine the type of emphasis
            let nodeName = this.identifyNodeName(emphasisClosable);
            let consumedEmphasis = this.countConsumedDelimiters(emphasisClosable);
            const areAllDelimiterConsumed = emphasisOpenCount <= consumedEmphasis

            if (areAllDelimiterConsumed) {
                // close the node
                nodeIterator.closeNodeAtIndex(openNodeIndex);

                openNode.name = nodeName

                // TODO cancel inner node
            } else {

                // create node for closed delimiter ()
                const closedDelimitersNode = new Node(nodeName, null, null, openNode.parent)
                    .addChildren(openNode.children)
                    .close()

                // partially close the current open delimiter node
                openNode.value = openNode.value.slice(0, emphasisOpenCount - consumedEmphasis)

                // replace open node children by the closed delimiter node
                openNode.children = [closedDelimitersNode]

                // set openNode as iterator parent
                nodeIterator.currentParentNode = openNode

            }

            emphasisToClose -= consumedEmphasis
            if(emphasisToClose <= 0) break
        }

        tokenIterator.eatTokens(1)
    }

    cancelAllOpenEmphasis(nodeIterator) {
        for(var i = nodeIterator.openNodes.length-1; i >= 0; i--) {
            if(nodeIterator.openNodes[i].name === NodeName.EMPHASIS_UNDEFINED) {
                nodeIterator.cancelNodeAtIndex(i)
            }
        }
    }

    calculateNumberOfDelimiterClosed(emphasisOpenCount, emphasisToClose) {
        let numberOfDelimiterNotClosed = emphasisOpenCount - emphasisToClose >= 0 ? emphasisOpenCount - emphasisToClose : 0
        return emphasisOpenCount - numberOfDelimiterNotClosed;
    }

    countConsumedDelimiters(emphasisClosable) {
        if (this.isBoldAndItalic(emphasisClosable)) {
            return  emphasisClosable
        }

        if (this.isBold(emphasisClosable)) {
            return emphasisClosable - emphasisClosable % 2
        }

        if (this.isItalic(emphasisClosable)) {
            return 1
        }
    }

    identifyNodeName(emphasisClosable) {
        if (this.isBoldAndItalic(emphasisClosable)) {
            return NodeName.EMPHASIS_BOLD_ITALIC
        }

        if (this.isBold(emphasisClosable)) {
            return NodeName.EMPHASIS_BOLD
        }

        if (this.isItalic(emphasisClosable)) {
            return  NodeName.EMPHASIS_ITALIC
        }
    }

    isBoldAndItalic(emphasisClosed) {
        return this.isOddNumber(emphasisClosed) && emphasisClosed > 2
    }

    isOddNumber(emphasisClosed) {
        return emphasisClosed % 2 !== 0;
    }

    isItalic(emphasisClosed) {
        return emphasisClosed === 1;
    }

    isBold(emphasisClosed) {
        return emphasisClosed > 1;
    }

    isPrecededByDelimiter(tokenIterator) {
        return tokenIterator.tokens[tokenIterator.index - 1].type === TokenType.DELIMITER;
    }

    isFollowedByDelimiter(tokenIterator) {
        return tokenIterator.tokens[tokenIterator.index + 1].type === TokenType.DELIMITER;
    }

}

export {emphasis, Emphasis}
